/** 工具栏筛选项宽度：按字段语义自动匹配。创建于 2026-08-28，最后修改：2026-09-14 */

const fieldSizeWidth = {
  xs: '76px',
  sm: '88px',
  md: '112px',
  phone: '132px',
  lg: '168px',
  xl: '240px',
  org: '280px',
  week: '280px',
  select: '128px',
  campus: '180px',
  date: '160px',
  datetime: '196px',
  daterange: '220px',
  datetimerange: '320px',
}

/** 筛选项 ≤ 该数量时工具栏尽量单行展示 */
export const toolbarSingleLineSearchThreshold = 5

const labelRules = [
  { pattern: /^周次$|选择周次/, size: 'week' },
  { pattern: /姓名|名字|人名/, size: 'md' },
  { pattern: /手机|电话|手机号/, size: 'phone' },
  { pattern: /卡号|学号|工号|号次|座位|编号/, size: 'md' },
  { pattern: /证件|身份证/, size: 'lg' },
  { pattern: /^SN$|序列号|设备号|终端号|终端标识|IMEI/, size: 'xl' },
  { pattern: /机构|学校|组织/, size: 'org' },
  { pattern: /标题|名称|关键字|关键词|设备名称/, size: 'xl' },
  { pattern: /关联校区|所属校区|校区/, size: 'campus' },
  { pattern: /班级|部门/, size: 'select' },
]

const keyRules = [
  { pattern: /^week$|^weekNo$|weekNumber|weekIndex/i, size: 'week' },
  { pattern: /stuName|teacherName|realName|userName|nickName|personName/i, size: 'md' },
  { pattern: /phone|mobile|tel/i, size: 'phone' },
  { pattern: /cardNo|cardNum|stuCode|seat|jobNo|workNo/i, size: 'md' },
  { pattern: /idCard|cert|identity/i, size: 'lg' },
  { pattern: /^sn$|serialNo|deviceSn|kqTerminalId|terminalId|imei/i, size: 'xl' },
  { pattern: /school|orgName|deptName|departmentName|schName|deviceName/i, size: 'org' },
  { pattern: /keyword|title/i, size: 'xl' },
  { pattern: /campusId|campus/i, size: 'campus' },
  { pattern: /classId|deptId|departmentId/i, size: 'select' },
]

const typeSizeMap = {
  input: 'xl',
  search: 'xl',
  select: 'select',
  cascader: 'lg',
  month: 'md',
  date: 'date',
  datetime: 'datetime',
  daterange: 'daterange',
  datetimerange: 'datetimerange',
}

function resolveFieldSize(item) {
  if (!item || (!item.label && !item.key && !item.type && !item.fieldSize)) return 'xl'
  if (item.fieldSize && fieldSizeWidth[item.fieldSize]) return item.fieldSize

  const label = String(item.label || '')
  for (let i = 0; i < labelRules.length; i++) {
    if (labelRules[i].pattern.test(label)) return labelRules[i].size
  }

  const key = String(item.key || '')
  for (let i = 0; i < keyRules.length; i++) {
    if (keyRules[i].pattern.test(key)) return keyRules[i].size
  }

  const type = item.type || 'input'
  return typeSizeMap[type] || 'md'
}

/** 去掉 placeholder / label 前的「请输入、请选择、选择」等前缀。创建于 2026-08-28 */
export function normalizeToolbarPlaceholderText(text) {
  return String(text || '')
    .replace(/^请输入/, '')
    .replace(/^请选择/, '')
    .replace(/^选择/, '')
    .replace(/[:：]\s*$/, '')
    .trim()
}

/** 工具栏筛选项标签：默认不带末尾冒号。最后修改：2026-08-29 */
export function normalizeToolbarLabel(text, options = {}) {
  if (text == null || text === '') return ''
  const normalized = String(text).replace(/[:：]\s*$/, '').trim()
  if (options.showLabelColon === true && normalized) return normalized + ':'
  return normalized
}

/**
 * 解析工具栏筛选项 placeholder：直接显示字段名，不带「请输入/请选择」。最后修改：2026-08-28
 */
export function resolveToolbarFieldPlaceholder(item, options = {}) {
  if (!item) return options.fallback || '关键字'

  if (item.placeholder) {
    const normalized = normalizeToolbarPlaceholderText(item.placeholder)
    if (normalized) return normalized
  }

  const label = normalizeToolbarPlaceholderText(item.label)
  const type = item.type || 'input'
  const isSelectLike = type === 'select' || type === 'cascader'
  const isDateLike = ['month', 'date', 'datetime', 'daterange', 'datetimerange'].includes(type)

  if (isSelectLike) {
    return label || '全部'
  }

  if (isDateLike) {
    if (type === 'daterange') return label || '日期范围'
    if (type === 'datetimerange') return label || '日期时间'
    return label || '日期'
  }

  return label || options.fallback || '关键字'
}

function widthFromPlaceholder(item, size) {
  const placeholder = resolveToolbarFieldPlaceholder(item, { fallback: '关键字' })
  const base = parseInt(fieldSizeWidth[size] || fieldSizeWidth.xl, 10)
  const estimated = placeholder.length * 14 + 48
  const maxPx = size === 'org' ? 320 : (size === 'campus' ? 260 : 280)
  const px = Math.max(base, Math.min(estimated, maxPx))
  return px + 'px'
}

/**
 * 解析工具栏筛选项宽度。创建于 2026-08-28
 */
export function resolveToolbarFieldWidth(item) {
  if (!item) return fieldSizeWidth.xl
  if (item.width) return item.width

  const size = resolveFieldSize(item)
  if (size === 'date' || size === 'datetime' || size === 'daterange' || size === 'datetimerange') {
    return fieldSizeWidth[size]
  }

  // 周次需完整展示日期区间，固定 280，不按 placeholder 压缩
  if (size === 'week') {
    return fieldSizeWidth.week
  }

  if (size === 'lg' || size === 'xl' || size === 'org' || size === 'campus' || !item.label) {
    return widthFromPlaceholder(item, size)
  }

  return fieldSizeWidth[size] || fieldSizeWidth.md
}

/**
 * 返回筛选项样式对象。创建于 2026-08-28
 */
export function resolveToolbarFieldStyle(item) {
  return { width: resolveToolbarFieldWidth(item) }
}

/**
 * 是否应尽量单行展示工具栏。创建于 2026-08-28
 */
export function shouldToolbarSingleLine(searchCount, options = {}) {
  const threshold = options.threshold == null ? toolbarSingleLineSearchThreshold : options.threshold
  return searchCount > 0 && searchCount <= threshold
}
